import clsx from 'clsx';
import React, { useState, Fragment, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Button from '@/src/components/common/Elements/Button/Button';
import Icon, { ICON_TYPES, ICONS } from '@/src/components/common/Elements/Icon/Icon';
import { RootState } from '@/src/reducers/types';
import { cn } from '@/src/util/ui';

import { Add } from './Actions/Add/Add';
import Search from './Actions/Search/Search';

import { LibraryActionEnum } from '../../../api/getProjectPermissions';
import { useLibrarySearchParamsContext } from '../../store/filters.store';
import { useLibraryListStore } from '../../store/library-list.store';

interface HeaderProps {
  breadcrumbs?: Array<{ id: string; name: string }>;
  projectId: string;
}

function Header({ breadcrumbs = [], projectId }: HeaderProps) {
  const dispatch = useDispatch();
  const files = useSelector((state: RootState) => state.files);
  const [onSearch, setOnSearch] = useState(false);
  const permissions = useLibraryListStore((s) => s.permissions);
  const { filter, clearSearchParams } = useLibrarySearchParamsContext();
  const isEditor = useMemo(() => {
    return permissions.library?.actions?.some(
      (action) => action.name === LibraryActionEnum.MANAGE_ACCESS && action.allowed
    );
  }, [permissions]);
  const hasLibraryTagsAccess = permissions.library.access;

  const inLibrary = files.activeInSidebar.id === 'library';
  const isShowClearFilters = filter.length > 0;

  const handleClickBreadcrumbs = (f: { id: string; name: string } | null, i: number) => {
    dispatch({
      type: 'UPDATE_FILES_ACTIVE_FOLDER',
      folder: f === null ? 'null' : { id: f.id, label: f.name },
    });

    breadcrumbs.splice(i + 1, breadcrumbs.length);
  };

  const handleSearch = () => {
    const hasFilters =
      Object.keys(files.filters.options).filter((f) => {
        return (
          (f === 'name' && files.filters.options[f]) ||
          (f === 'uploadedDate' &&
            (files.filters.options[f].from.value || files.filters.options[f].to.value)) ||
          (files.filters.options[f] || {}).value
        );
      }).length > 0;

    setOnSearch(hasFilters);
  };

  const handleCancel = () => {
    setOnSearch(false);
  };

  const handleClickClearFilterButton = () => {
    clearSearchParams({ filter: undefined });
  };

  return (
    <div className="filesHeader">
      {!onSearch && (
        <div className="filesTileContainer">
          {!inLibrary && (
            <span className="tw-text-[16px] tw-font-brandonTextMedium">
              {files.activeInSidebar.label}
            </span>
          )}
          {inLibrary && (
            <div className="breadcrumbs">
              <div className="breadcrumb tw-flex">
                <a onClick={() => handleClickBreadcrumbs(null, -1)}>Library</a>
                {breadcrumbs.length > 0 && <span className="tw-self-center">{`>`}</span>}
                {breadcrumbs.map((b, i) => {
                  const isLastItem = i === breadcrumbs.length - 1;
                  return (
                    <Fragment key={i}>
                      <div
                        className={cn(
                          'tw-inline-flex tw-items-center tw-ml-1',
                          isLastItem && 'tw-text-cfo-blue-600'
                        )}
                      >
                        <i className="material-icons">folder</i>
                        <a
                          onClick={() => handleClickBreadcrumbs(b, i)}
                          className={cn('tw-ml-[3px]', isLastItem && '!tw-text-cfo-blue-600 ')}
                        >
                          {b.name}
                        </a>
                        {i <= breadcrumbs.length - 2 && <span>{`>`}</span>}
                      </div>
                    </Fragment>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      {isEditor && onSearch && (
        <div className="filesTileContainer">
          <p>Search Results</p>
        </div>
      )}
      {(isEditor || hasLibraryTagsAccess) && (
        <div className="filesActionsContainer">
          <div>
            {isShowClearFilters && (
              <Button
                className={clsx(
                  'button--clear_filter',
                  'tw-max-w-[108px] tw-w-full tw-h-[32px]',
                  'tw-p-[5px] tw-mr-[8px]',
                  'hover:tw-underline hover:tw-text-cfo-blue-600'
                )}
                buttonClassSettings={{
                  variant: 'style_unset',
                  position: 'center',
                  size: 'sm',
                  weight: 'bold',
                }}
                onClick={handleClickClearFilterButton}
              >
                <Icon
                  icon={ICONS.clear_filter({ className: 'group-hover:tw-fill-cfo-blue-600' })}
                  iconType={ICON_TYPES.outlined}
                  className={clsx('tw-mr-[5px]')}
                />
                Clear Filters
              </Button>
            )}
          </div>
          <div className="fileSearch">
            <Search projectId={projectId} onSearch={handleSearch} onCancel={handleCancel} />
          </div>

          <div className="filesUpload">
            <Add />
          </div>
        </div>
      )}
    </div>
  );
}

export default Header;
